import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Phone, UserCheck } from "lucide-react";

const SRC = { fidelity: "Fidelity", ristorante: "Ristorante", spa: "Spa", massaggi: "Massaggi", hotel: "Hotel", bar: "Bar", cavallo: "Cavallo", eventi: "Eventi", manuale: "Archivio", passaggio: "Passaggio", altro: "Altro" };

// Name/phone input with live suggestions from the unified customer database (avoids duplicates, speeds up entry).
// `search(q)` must resolve {items:[{key,name,phone,email,sources,visits,level_it,last_activity}]}.
export function CustomerAutocomplete({ value, onChange, onPick, search, placeholder = "Nome ospite", className = "", testId = "customer-ac", autoFocus = false, inputMode, minChars = 2 }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const timer = useRef(null);
  const box = useRef(null);
  const typed = useRef(false); // search only after user typing, not after programmatic fills (pick)

  useEffect(() => {
    clearTimeout(timer.current);
    const q = (value || "").trim();
    if (!typed.current) return undefined;
    typed.current = false;
    if (q.length < minChars) { setItems([]); return undefined; }
    timer.current = setTimeout(() => {
      search(q).then((d) => { setItems(d.items || []); setOpen(true); setHi(-1); }).catch(() => setItems([]));
    }, 220);
    return () => clearTimeout(timer.current);
  }, [value, search, minChars]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const pick = (c) => { onPick(c); setOpen(false); setItems([]); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={box}>
      <input value={value} onChange={(e) => { typed.current = true; onChange(e.target.value); }} onFocus={() => items.length && setOpen(true)} onKeyDown={onKey}
        placeholder={placeholder} className={className} data-testid={testId} autoFocus={autoFocus} inputMode={inputMode} autoComplete="off" />
      {open && items.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-2xl border border-[#e8dcc8] bg-white shadow-xl py-1" data-testid={`${testId}-list`}>
          {items.map((c, i) => (
            <li key={c.key}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)} data-testid={`${testId}-item-${i}`}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 ${i === hi ? "bg-[#faf3ee]" : "hover:bg-[#faf3ee]"}`}>
                <UserCheck className="h-4 w-4 text-[#C48B31] shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-bold text-sm text-[#2b1410] truncate">{c.name}</span>
                  <span className="block text-[11px] text-[#7c5a4e] truncate inline-flex items-center gap-1.5">
                    {c.phone && <span className="inline-flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" /> {c.phone}</span>}
                    {c.visits > 0 && <span>· {c.visits} visite</span>}
                    {c.last_activity && <span>· ultima {c.last_activity.split("-").reverse().join("/")}</span>}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1 justify-end max-w-[40%]">
                  {c.level_it && <span className="rounded-full bg-[#4a1c17] text-white px-1.5 py-0.5 text-[10px] font-bold inline-flex items-center gap-0.5"><Sparkles className="h-2.5 w-2.5" /> {c.level_it}</span>}
                  {(c.sources || []).filter((s) => s !== "fidelity").slice(0, 3).map((s) => <span key={s} className="rounded-full bg-[#faf3ee] text-[#7c5a4e] px-1.5 py-0.5 text-[10px] font-semibold">{SRC[s] || s}</span>)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
